"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  PayPalButtons,
  usePayPalScriptReducer,
  FUNDING,
  PayPalButtonsComponentProps,
} from "@paypal/react-paypal-js";
import type {
  CreateOrderData,
  CreateOrderActions,
  OnApproveData,
  OnApproveActions,
} from "@paypal/paypal-js";
import { cn } from "@/lib/utils";
import { usePayPalCheckout } from "@/hooks/usePaypalCheckout";
import { usePaypalContext } from "@/components/checkout/paypal-provider";
import { useIsMutating } from "@tanstack/react-query";
import { PayPalButtonsWithResume } from "../paypal-buttons-with-resume";
import { FLUID_CDN_URL } from "@/lib/constants";
import {
  useApplePayPalCheckout,
  PayPalApplePayConfig,
} from "@/hooks/usePPCPApplePay";

interface UsePaypalProps {
  fastCheckout?: boolean;
  cartToken?: string;
  onSuccess?: () => Promise<void>;
  onError?: (error: Error) => void;
  onRedirect?: (url: string) => void;
  paymentMethodsIncludes?: string | null;
  disabled?: boolean;
  deviceData?: string;
  kountSessionId?: string;
}

interface PayPalButtonRendererProps extends UsePaypalProps {
  fundingSource:
    | typeof FUNDING.PAYPAL
    | typeof FUNDING.VENMO
    | typeof FUNDING.PAYLATER;
  isVenmo?: boolean;
  isPayLater?: boolean;
  className?: string;
}

const PayPalButtonSkeleton = ({ className }: { className?: string }) => {
  return (
    <div className={cn("h-input w-full overflow-hidden rounded", className)}>
      <div className="h-full w-full animate-pulse rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />
    </div>
  );
};

type PayPalButtonsOptions = React.ComponentProps<typeof PayPalButtons>;

const PayPalButtonRenderer = ({
  className,
  fundingSource,
  isVenmo = false,
  isPayLater = false,
  cartToken,
  onSuccess,
  onError,
  onRedirect,
  paymentMethodsIncludes,
  fastCheckout,
  disabled,
}: PayPalButtonRendererProps) => {
  const [{ isPending, isResolved }] = usePayPalScriptReducer();

  const {
    isProcessing,
    createOrder,
    onApprove,
    onPayPalError,
    onShippingAddressChange,
    onShippingOptionsChange,
  } = usePayPalCheckout({
    onSuccess,
    onError,
    onRedirect,
    paymentMethodsIncludes,
    fastCheckout,
  });

  const isSubscriptionPending =
    useIsMutating({ mutationKey: ["update-item-subscription", cartToken] }) > 0;
  const isQuantityPending =
    useIsMutating({ mutationKey: ["update-item-quantity", cartToken] }) > 0;
  const isDiscountPending =
    useIsMutating({ mutationKey: ["apply-discount", cartToken] }) > 0;
  const isRemoveDiscountPending =
    useIsMutating({ mutationKey: ["remove-discount", cartToken] }) > 0;

  const isCartMutating =
    isSubscriptionPending ||
    isQuantityPending ||
    isDiscountPending ||
    isRemoveDiscountPending;

  const buttonStyles: PayPalButtonsComponentProps["style"] = useMemo(
    () => ({
      shape: "rect",
      layout: "vertical",
      height: 48,
      ...(fastCheckout && { color: "gold", tagline: false }),
    }),
    [fastCheckout],
  );

  const venmoButtonStyles: PayPalButtonsComponentProps["style"] = useMemo(
    () => ({
      shape: "rect",
      layout: "vertical",
      height: 48,
      color: "blue",
      tagline: false,
    }),
    [],
  );

  const payLaterButtonStyles: PayPalButtonsComponentProps["style"] = useMemo(
    () => ({
      shape: "rect",
      layout: "vertical",
      height: 48,
      color: "white",
      tagline: false,
    }),
    [],
  );

  const getButtonStyle =
    useCallback((): PayPalButtonsComponentProps["style"] => {
      if (isPayLater) return payLaterButtonStyles;
      if (isVenmo) return venmoButtonStyles;
      return buttonStyles;
    }, [
      isPayLater,
      isVenmo,
      buttonStyles,
      venmoButtonStyles,
      payLaterButtonStyles,
    ]);

  const memoOptions = useMemo<PayPalButtonsOptions>(() => {
    return {
      fundingSource,
      style: getButtonStyle(),
      className,
      disabled: isProcessing || !cartToken || isCartMutating || disabled,

      createOrder: (data: CreateOrderData, actions: CreateOrderActions) => {
        if (!cartToken) {
          return Promise.reject(new Error("Missing cart token"));
        }
        return createOrder(data, actions, cartToken);
      },

      onApprove: (data: OnApproveData, actions: OnApproveActions) => {
        if (!cartToken) {
          return Promise.resolve();
        }
        return onApprove(data, actions, cartToken);
      },

      onError: (error: Record<string, unknown>) => {
        console.error("PayPal SDK error:", error);
        try {
          onPayPalError(error);
        } catch (e) {
          console.error("Error in onPayPalError handler:", e);
        }
      },

      onShippingAddressChange: (data, actions) => {
        if (!cartToken) {
          return Promise.resolve();
        }
        return onShippingAddressChange(data, actions, cartToken);
      },

      onShippingOptionsChange: (data, actions) => {
        if (!cartToken) {
          return Promise.resolve();
        }
        return onShippingOptionsChange(data, actions, cartToken);
      },

      onCancel: () => {},
    };
  }, [
    fundingSource,
    getButtonStyle,
    className,
    isProcessing,
    cartToken,
    isCartMutating,
    disabled,
    createOrder,
    onApprove,
    onPayPalError,
    onShippingAddressChange,
    onShippingOptionsChange,
  ]);

  if (isPending || !isResolved || isProcessing) {
    return <PayPalButtonSkeleton className={className} />;
  }

  if (!cartToken) {
    return null;
  }

  if (!createOrder || !onApprove) {
    return <PayPalButtonSkeleton className={className} />;
  }

  return <PayPalButtonsWithResume options={memoOptions} />;
};

PayPalButtonRenderer.displayName = "PayPalButtonRenderer";

// ========== APPLE PAY BUTTON RENDERER (using PayPal) ==========
// Note: Eligibility is already checked by PaypalProvider, this component just needs the config
const ApplePayButtonRenderer = React.memo<{
  fastCheckout?: boolean;
  cartToken?: string;
  onSuccess?: () => Promise<void>;
  onError?: (error: Error) => void;
  onRedirect?: (url: string) => void;
  paymentMethodsIncludes?: string | null;
  className?: string;
  disabled?: boolean;
  deviceData?: string;
  kountSessionId?: string;
}>(
  ({
    className,
    cartToken,
    onSuccess,
    onError,
    onRedirect,
    paymentMethodsIncludes,
    disabled,
    deviceData,
    kountSessionId,
  }) => {
    const [{ isPending: isScriptLoading }] = usePayPalScriptReducer();
    const [config, setConfig] = useState<PayPalApplePayConfig | null>(null);
    const [isLoadingConfig, setIsLoadingConfig] = useState(true);

    const { isProcessing, handleApplePayPalCheckout } = useApplePayPalCheckout({
      onSuccess,
      onError,
      onRedirect,
      paymentMethodsIncludes,
      deviceData,
      kountSessionId,
    });

    // Get Apple Pay config from PayPal SDK.
    useEffect(() => {
      if (isScriptLoading) return;

      const getConfig = async () => {
        try {
          const paypal =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as any).paypal_checkout_global || (window as any).paypal;
          if (paypal?.Applepay) {
            const sdkConfig = await paypal.Applepay().config();
            setConfig(sdkConfig);
          }
        } catch (err) {
          console.error("Failed to get Apple Pay config:", err);
        } finally {
          setIsLoadingConfig(false);
        }
      };

      getConfig();
    }, [isScriptLoading]);

    const handleApplePayClick = () => {
      if (!cartToken || !config) return;
      handleApplePayPalCheckout(cartToken, config);
    };

    // Show skeleton while loading
    if (isScriptLoading || isLoadingConfig || isProcessing) {
      return <PayPalButtonSkeleton className={className} />;
    }

    // Don't show if config failed to load
    if (!config) return null;

    return (
      <button
        type="button"
        className={cn(
          "h-input flex w-full items-center justify-center rounded bg-black py-2 text-white transition-colors duration-200 hover:bg-gray-600",
          className,
        )}
        onClick={handleApplePayClick}
        disabled={!cartToken || isProcessing || disabled}
      >
        <img
          src={`${FLUID_CDN_URL}/images/checkout/icons/ApplePayWhite.png`}
          alt="Apple Pay"
          width={50}
          height={50}
        />
      </button>
    );
  },
);
ApplePayButtonRenderer.displayName = "ApplePayButtonRenderer";

// ========== MAIN HOOK ==========
export default function usePaypal(props: UsePaypalProps) {
  const {
    fastCheckout,
    cartToken,
    onSuccess,
    onError,
    onRedirect,
    paymentMethodsIncludes,
    disabled,
    deviceData,
    kountSessionId,
  } = props;

  const {
    shouldShowVenmo,
    shouldShowPayLater,
    shouldShowApplePay,
    isProviderReady,
    isRejected,
  } = usePaypalContext();

  if (!isProviderReady || isRejected) {
    return {
      paypalButton: null,
      venmoButton: null,
      payLaterButton: null,
      applePayButton: null,
    };
  }

  return {
    paypalButton: (
      <PayPalButtonRenderer
        fastCheckout={fastCheckout}
        cartToken={cartToken}
        onSuccess={onSuccess}
        onError={onError}
        onRedirect={onRedirect}
        paymentMethodsIncludes={paymentMethodsIncludes}
        disabled={disabled}
        fundingSource={FUNDING.PAYPAL}
        className={cn(!fastCheckout && "mt-6 mb-[-6px]")}
      />
    ),
    venmoButton: shouldShowVenmo ? (
      <PayPalButtonRenderer
        fastCheckout={fastCheckout}
        cartToken={cartToken}
        onSuccess={onSuccess}
        onError={onError}
        onRedirect={onRedirect}
        paymentMethodsIncludes={paymentMethodsIncludes}
        disabled={disabled}
        fundingSource={FUNDING.VENMO}
        isVenmo
        className={cn(!fastCheckout && "mt-3")}
      />
    ) : null,
    payLaterButton: shouldShowPayLater ? (
      <PayPalButtonRenderer
        fastCheckout={fastCheckout}
        cartToken={cartToken}
        onSuccess={onSuccess}
        onError={onError}
        onRedirect={onRedirect}
        paymentMethodsIncludes={paymentMethodsIncludes}
        disabled={disabled}
        fundingSource={FUNDING.PAYLATER}
        isPayLater
        className={cn(!fastCheckout && "mt-3")}
      />
    ) : null,
    applePayButton: shouldShowApplePay ? (
      <ApplePayButtonRenderer
        fastCheckout={fastCheckout}
        cartToken={cartToken}
        onSuccess={onSuccess}
        onError={onError}
        onRedirect={onRedirect}
        paymentMethodsIncludes={paymentMethodsIncludes}
        disabled={disabled}
        deviceData={deviceData}
        kountSessionId={kountSessionId}
        className={cn(!fastCheckout && "mt-3")}
      />
    ) : null,
  };
}
